import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { styled } from '@mui/system';
import simpleheat from 'simpleheat';

import {
  TAiSimulationPartialDto,
  TPersonaConfigDto,
  TPrototypePartialDto,
} from '@app-velocity/av-api-v2';

import { Loader } from '@app-velocity/animations';

import { PageAction, PreviewHighlightOverlay } from '@app-velocity/common';

import { useWorker } from '../ViewPreview/hooks/useWorker';
import { WorkerPage, WorkerPartial, WorkerPartialResult } from '../../types';
import { DebugLayer } from './components/DebugLayer';

export type ViewPreviewProps = {
  partialsResults: TPrototypePartialDto[];
  partials: TAiSimulationPartialDto[];
  page: {
    width: number;
    height: number;
    density: number;
    image: string;
    actions?: PageAction[];
  };
  clicks?: { x: number; y: number }[];
  size: 'full' | 'large' | 'normal';
  persona: 'accessible' | 'default' | string;
  personaConfig?: TPersonaConfigDto;
  noFilters?: boolean;
  highlight?: boolean;
};

const StyledViewPreview = styled('div', {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  objectFit: 'contain',
  padding: highlight ? theme.spacing(4) : 0,
  borderRadius: highlight ? theme.spacing(2) : 0,
  backgroundColor: highlight
    ? 'var(--background-background-secondary-color)'
    : 'transparent',
}));

const StyledCanvas = styled('canvas')<{ ready: boolean }>(
  ({ theme, ready }) => ({
    display: 'block',
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    boxShadow: `0px ${theme.spacing(2)} ${theme.spacing(
      3
    )} 0px rgba(0, 0, 0, 0.1)`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    opacity: ready ? 1 : 0,
    transition: 'opacity .3s',
  })
);

const CanvasContainer = styled('div')(() => ({
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  maxWidth: '100%',
  maxHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CanvasWrapper = styled('div')({
  position: 'relative',
  flex: '0 0 auto',
  maxWidth: '100%',
  maxHeight: '100%',
});

const LoaderContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  // background: 'rgba(0, 0, 0, 0.5)',
  width: theme.spacing(20),
  height: theme.spacing(20),
  left: '50%',
  top: '50%',
  borderRadius: theme.spacing(2),
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
}));

const getSize = (size: 'full' | 'large' | 'normal') => {
  switch (size) {
    case 'full':
      return document.body.offsetHeight - 100;
    case 'large':
      return 500;
    default:
      return 250;
  }
};

const getFittedCanvasSize = (
  pageWidth: number,
  pageHeight: number,
  size: ViewPreviewProps['size'],
  parentWidth = 0,
  parentHeight = 0
) => {
  const preferredHeight = getSize(size);
  const viewportWidth =
    typeof window === 'undefined' ? preferredHeight : window.innerWidth;
  const viewportHeight =
    typeof window === 'undefined' ? preferredHeight : window.innerHeight;
  const maxWidth = Math.min(
    viewportWidth,
    parentWidth > 0 ? parentWidth : viewportWidth
  );
  const maxHeight = Math.min(
    preferredHeight,
    viewportHeight,
    parentHeight > 0 ? parentHeight : preferredHeight
  );
  const scale = Math.min(maxWidth / pageWidth, maxHeight / pageHeight);

  return {
    width: Math.max(1, Math.round(pageWidth * scale)),
    height: Math.max(1, Math.round(pageHeight * scale)),
  };
};

export const ViewPreviewContent = memo(
  ({
    page,
    partials,
    partialsResults,
    clicks,
    size,
    persona,
    personaConfig,
    noFilters = false,
    highlight: isHighlighted = false,
  }: ViewPreviewProps) => {
    const ref = useRef<HTMLCanvasElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasPixelSizeRef = useRef<{ width: number; height: number } | null>(
      null
    );

    const [dataId] = useState(
      () => new Date().getTime() + Math.round(Math.random() * 1000)
    );
    const [parentSize, setParentSize] = useState<{
      width: number;
      height: number;
    } | null>(null);

    const { width: targetWidth, height: targetHeight } =
      page?.width && page?.height
        ? getFittedCanvasSize(
            page.width,
            page.height,
            size,
            parentSize?.width,
            parentSize?.height
          )
        : { width: 0, height: 0 };

    const { ready, done, postMessage } = useWorker({
      canvasRef: ref,
      enabled: !!(parentSize && targetWidth && targetHeight),
    });

    if (
      parentSize &&
      !canvasPixelSizeRef.current &&
      targetWidth &&
      targetHeight
    ) {
      canvasPixelSizeRef.current = {
        width: targetWidth,
        height: targetHeight,
      };
    }

    const canvasToImageBitmap = async (
      canvas: HTMLCanvasElement
    ): Promise<ImageBitmap> => {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      );
      if (!blob) throw new Error('Failed to convert canvas to Blob');

      return await createImageBitmap(blob);
    };

    const transformData = (data: { x: number; y: number }[]) => {
      const map: { [coord: string]: number } = {};

      data.forEach((point) => {
        const x = Math.round(point.x || 0);
        const y = Math.round(point.y || 0);

        map[`${x}|${y}`] = (map[`${x}|${y}`] || 0) + 1;
      });

      return Object.keys(map).map((key) => {
        const splited = key.split('|');

        return [Number(splited[0]), Number(splited[1]), map[key]];
      });
    };

    const generateHeatmap = async (
      clicks: { x: number; y: number }[],
      page: { image: string; width: number; height: number; density: number }
    ): Promise<ImageBitmap | null> => {
      if (!clicks.length || !targetWidth || !targetHeight) return null;

      const factor = page.height / targetHeight;
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scaledClicks = clicks.map((point) => ({
        x: point.x / factor,
        y: point.y / factor,
      }));

      const heat = simpleheat(canvas);
      heat.data([...transformData(scaledClicks)]);
      heat.max(3);
      heat.radius(25, 15);
      heat.draw(0.25);

      return await canvasToImageBitmap(canvas);
    };

    const handleRender = async (
      clicks: { x: number; y: number }[],
      page: { width: number; height: number; image: string; density: number },
      partials: TAiSimulationPartialDto[],
      partialsResults: TPrototypePartialDto[],
      persona: 'accessible' | 'default' | string,
      personaConfig?: TPersonaConfigDto
    ) => {
      const heatMap = await generateHeatmap(clicks || [], page);

      postMessage(
        {
          type: 'render',
          page: {
            image: page.image,
            width: page.width,
            height: page.height,
          } as WorkerPage,
          partials: partials as WorkerPartial[],
          partialsResults: partialsResults.map((p) => ({
            id: p.id,
            image: p.image,
          })) as WorkerPartialResult[],
          size,
          heatMap,
          persona,
          personaConfig,
          bodyHeight: document.body.offsetHeight,
          targetWidth,
          targetHeight,
          noFilters,
        },
        heatMap ? [heatMap] : []
      );
    };

    useLayoutEffect(() => {
      const node = containerRef.current;

      if (!node) return;

      const updateParentSize = () => {
        const nextSize = {
          width: Math.round(node.clientWidth),
          height: Math.round(node.clientHeight),
        };

        setParentSize((current) => {
          if (
            current &&
            current.width === nextSize.width &&
            current.height === nextSize.height
          ) {
            return current;
          }

          return nextSize;
        });
      };

      updateParentSize();
      const observer = new ResizeObserver(updateParentSize);
      observer.observe(node);
      window.addEventListener('resize', updateParentSize);

      return () => {
        observer.disconnect();
        window.removeEventListener('resize', updateParentSize);
      };
    }, []);

    useEffect(() => {
      if (!page || !partialsResults || !partials) return;
      if (!parentSize || !ready || !targetWidth || !targetHeight) return;
      handleRender(
        clicks || [],
        page,
        partials,
        partialsResults,
        persona,
        personaConfig
      );
    }, [
      page,
      partials,
      partialsResults,
      clicks,
      size,
      persona,
      personaConfig,
      parentSize,
      ready,
      targetWidth,
      targetHeight,
    ]);

    if (!page) return null;

    if (!page.width || !page.height) return null;

    const canvasWidth = canvasPixelSizeRef.current?.width ?? targetWidth;
    const canvasHeight = canvasPixelSizeRef.current?.height ?? targetHeight;

    return (
      <StyledViewPreview highlight={isHighlighted}>
        {(!ready || !done) && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}
        <DebugLayer
          page={{
            width: page.width,
            height: page.height,
            actions: page.actions || [],
          }}
          partials={partials}
          viewPartials={partialsResults}
        />
        <CanvasContainer ref={containerRef}>
          {parentSize && targetWidth && targetHeight ? (
            <CanvasWrapper style={{ width: targetWidth, height: targetHeight }}>
              <StyledCanvas
                ready={ready && done}
                data-id={dataId}
                ref={ref}
                width={canvasWidth}
                height={canvasHeight}
              />
              <PreviewHighlightOverlay />
            </CanvasWrapper>
          ) : null}
        </CanvasContainer>
      </StyledViewPreview>
    );
  }
);
